import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { generatePath, useHistory } from 'react-router-dom'

import {
  fetchClaimDevicesThunk,
  fetchInventoryThunk,
} from 'features/networkAi/slice'
import { fetchHomeThunk } from 'features/home/slice'
import { commonMessages } from 'app/i18n/messages'
import { useAppSelector } from 'app/hooks'
import routes from 'app/routes'
import {
  inventoryFetchDataSelector,
  mistOrganizationIdSelector,
  subsribedAccessPointsCountSelector,
} from 'features/networkAi/selectors'
import {
  useCurrentHomeSiteId,
  useNetworkHomeJobsRouteParams,
} from 'features/networkAi/hooks'
import { SpinnerCentered } from 'components/Loader'

import NetworkWizardLayout from '../NetworkWizardLayout'
import {
  NetworkWizardBottomPanel,
  NetworkWizardCancelButton,
  NetworkWizardSubmitButton,
  NetworkWizardTitle,
} from '../styles'
import SubscriptionStatusDashboard from '../SubscriptionStatusDashboard'
import { SubscriptionStatusHeaderButton } from '../SubscriptionStatusDashboard/styles'
import DeviceListTable from './DeviceListTable'
import { purchaseSubscriptionMessages } from '../PurchaseDeviceSubscription/messages'

function DevicesList() {
  const { rootDomainId, homeId, jobId } = useNetworkHomeJobsRouteParams()
  const organizationId = useAppSelector(mistOrganizationIdSelector)

  const siteId = useCurrentHomeSiteId({ homeId })
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const history = useHistory()

  const isLoadingDevices = useAppSelector(inventoryFetchDataSelector.isFetching)
  const inventoryList = useAppSelector(inventoryFetchDataSelector.data)
  const subscribedCount = useAppSelector(subsribedAccessPointsCountSelector)

  const isSuccessClaimedAll = useMemo(() => {
    if (inventoryList) {
      return subscribedCount === inventoryList.length
    }

    return 0
  }, [inventoryList, subscribedCount])

  const showClaimDeviceButton = useMemo(() => {
    if (inventoryList) {
      return !isSuccessClaimedAll && subscribedCount > inventoryList.length
    }
  }, [inventoryList, isSuccessClaimedAll, subscribedCount])

  const handleClickToSkip = useCallback(() => {
    const path = generatePath(routes.networkAi.dashboard.root, {
      rootDomainId,
      jobId,
      homeId,
    })

    history.push(path)
  }, [history, homeId, jobId, rootDomainId])

  const handleClickToClaimDevice = useCallback(() => {
    const path = generatePath(routes.networkAi.wizard.claimDevice, {
      rootDomainId,
      jobId,
      homeId,
    })

    history.push(path)
  }, [history, homeId, jobId, rootDomainId])

  const handleClickToDoneClaiming = useCallback(() => {
    const path = generatePath(routes.networkAi.wizard.createWLANs, {
      rootDomainId,
      jobId,
      homeId,
    })

    history.push(path)
  }, [history, homeId, jobId, rootDomainId])

  useEffect(() => {
    if (siteId) dispatch(fetchClaimDevicesThunk(siteId))
    if (organizationId) dispatch(fetchInventoryThunk(siteId))
    dispatch(fetchHomeThunk(homeId))
  }, [dispatch, siteId, homeId, organizationId])

  const bottomBlock = useMemo(() => {
    return (
      <NetworkWizardBottomPanel>
        <NetworkWizardCancelButton
          variant="secondary"
          type="button"
          onClick={handleClickToSkip}
        >
          {formatMessage(purchaseSubscriptionMessages.claimDevices)}
        </NetworkWizardCancelButton>

        <NetworkWizardSubmitButton
          variant="primary"
          type="submit"
          disabled={false}
          onClick={handleClickToDoneClaiming}
        >
          Done Claiming
        </NetworkWizardSubmitButton>
      </NetworkWizardBottomPanel>
    )
  }, [formatMessage, handleClickToDoneClaiming, handleClickToSkip])

  return (
    <NetworkWizardLayout bottomBlock={bottomBlock} currentWizardStepId={3}>
      <NetworkWizardTitle>Claim Devices</NetworkWizardTitle>
      {isLoadingDevices || !siteId ? (
        <SpinnerCentered />
      ) : (
        <>
          {showClaimDeviceButton && (
            <SubscriptionStatusHeaderButton
              variant="primary"
              onClick={handleClickToClaimDevice}
            >
              {formatMessage(commonMessages.skip)}
            </SubscriptionStatusHeaderButton>
          )}
          <SubscriptionStatusDashboard
            showClaimDeviceButton={showClaimDeviceButton}
            subscribedCount={subscribedCount}
            claimedCount={inventoryList?.length || 0}
            showStatusInformation
          />
          <DeviceListTable devices={inventoryList || []} />
        </>
      )}
    </NetworkWizardLayout>
  )
}

export default DevicesList
